// T1 — MEMBERSHIP TIERS, AS DATA.
//
// THE OWNER'S STRUCTURE, in ONE place. Everything about who may open what lives here: the three
// tiers, their prices, what each one unlocks, the free layer every signed-up member keeps forever,
// the one-time products, and the Ask Lala fair-use caps. Nothing about tiers is decided anywhere
// else, because a rule scattered across thirty call sites is a rule nobody can move.
//
// **TO MOVE A FEATURE BETWEEN TIERS: change its line in `FEATURES` below. That is the whole job.**
// No rebuild of the gating, no hunt through the server, no client change. The membership page, the
// locked-door box and the server-side gate all read this file, so they can never disagree about
// what a tier includes.
//
// THE DATA-SECURITY LAW: gating is decided SERVER-SIDE from the member's own subscription row. The
// client is told what it may show; it is never believed about what it may open.

// W1-2: the refusal COPY is composed in its own module, which imports nothing. This file imports it,
// never the other way round, so the arithmetic of who-opens-what stays in one place and the wording
// of the no stays in another.
import { upgradeCopy, purchaseCopy, phraseForLabel } from "./upgrade-copy.js";

// ---------------------------------------------------------------------------
// THE LADDER. Each tier INHERITS everything below it — spelled out once here, so "everything in
// Newbie" is a fact the code knows rather than a sentence on a marketing page.
// ---------------------------------------------------------------------------
export const TIERS = [
  {
    key: "newbie",
    label: "Newbie",
    price: 39,
    priceText: "$39",
    period: "month",
    inherits: null,
    tagline: "Your chart, your numerology, your daily practice.",
  },
  {
    key: "apprentice",
    label: "Apprentice",
    price: 69,
    priceText: "$69",
    period: "month",
    inherits: "newbie",
    // W2-2(b): it claimed "and Lala herself" and she took that out. Ask Lala stays a line item, which
    // is where it belongs: the tagline is not the place a member should be reading what she gets.
    tagline: "Everything in Newbie.",
  },
  {
    key: "vet",
    label: "Vet",
    price: 149,
    priceText: "$149",
    period: "month",
    inherits: "apprentice",
    tagline: "Everything in Apprentice, and the whole practice.",
  },
];

export const TIER_ORDER = ["free", "newbie", "apprentice", "vet"];
export const tierRank = (key) => {
  const i = TIER_ORDER.indexOf(String(key || "free"));
  return i < 0 ? 0 : i;
};
export const tierByKey = (key) => TIERS.find((t) => t.key === key) || null;

// ---------------------------------------------------------------------------
// **HER RULING, 2026-08-07: AN ELITE NEVER PAYS FULL PRICE FOR VET. THE DIFFERENCE, NOT THE STICKER.**
//
// A complimentary GRANT counts as a held tier for upgrade pricing, exactly like a paid one. Until
// this existed the two were different records with different fates: `holdsSubscription` reads
// `stripe_subscription_id`, a grant has none, so a granted Apprentice upgrading to Vet fell past the
// proration branch into a FRESH full-price checkout — nothing to prorate against, because Stripe can
// only prorate a subscription that exists. Esperanza was charged $149 for an $80 step.
//
// **THE CREDIT IS THE HELD TIER'S OWN MONTHLY PRICE, ONCE.** $149 - $69 = $80 today, Vet active
// instantly, full $149 next cycle — her three sentences, and the arithmetic is derived from the
// ladder above rather than typed, so a price change moves the credit with it (R31). It generalises
// by rank, which is what *"same principle for any granted tier upgrading anywhere"* asks for.
//
// **A DOWNGRADE OR A SIDEWAYS MOVE EARNS NOTHING (R18).** *Owes less* and *owes nothing* are
// different facts, and a credit that could exceed the price would hand out money: the floor is zero
// and it is arithmetic here rather than a hope about which tiers exist.
export function grantUpgradeCredit({ heldTier = "", targetTier = "" } = {}) {
  const held = tierByKey(String(heldTier || ""));
  const target = tierByKey(String(targetTier || ""));
  if (!held || !target) return 0;
  if (tierRank(target.key) <= tierRank(held.key)) return 0;
  const credit = Math.min(Number(held.price) || 0, Number(target.price) || 0);
  return credit > 0 ? credit : 0;
}

/** What she is actually charged today for that step, in whole dollars. Never below zero. */
export function grantUpgradeDue({ heldTier = "", targetTier = "" } = {}) {
  const target = tierByKey(String(targetTier || ""));
  if (!target) return 0;
  return Math.max(0, (Number(target.price) || 0) - grantUpgradeCredit({ heldTier, targetTier }));
}

// ---------------------------------------------------------------------------
// THE FEATURE MAP. `tier` is the LOWEST tier that opens it. Change one line to move a feature.
//
// `label` is what the member is told she is getting, so it doubles as the membership page's list
// and the locked-door box's contents — one source, so the page can never promise something the
// gate does not actually open.
//
// T8-1 — `enforcedIn` SAYS WHERE THE GATE ACTUALLY LIVES. Default is `"route"`: the feature costs a
// URL in server/route-features.js, and the central gate holds it shut before any handler runs. Two
// features could not be enforced that way, and rather than let them sit silently unmapped (which is
// how the owner's paid-Newbie walk found Shelves and Affirmations refusing her), each one DECLARES
// where its gate is, in this table, and the fixtures read the declaration instead of carrying a
// hard-coded list of exceptions that nobody maintains:
//   • "handler" — one URL serves several tiers' worth of things, so only the handler knows which
//     feature THIS request costs (the report type, the drawer kind). It calls requireFeature itself.
//   • "shell"   — there is no serving route at all, because the content behind it is already free by
//     another law. The gate is presentation only. NOTHING that costs money may be declared "shell".
//
// W2-2(a) — THE WORD "DRAWER" IS OUT OF EVERY TIER LINE ITEM (owner's edit, folded into the W1-5
// rebuild). It is done HERE, at the source, and not in the box, because this table is what the
// membership page, the locked-door box, the refusal copy AND the FAQ all read. Six labels changed:
// Affirmations, Verses, Chants, Incantations, Angels, and `lala:chat`, which read "Ask Lala, the page
// and the drawer" and now reads "Ask Lala" exactly as her own artwork does. Her enumeration named
// five; the rule she wrote above it was "every tier line item", and that sixth one was one. Because
// the label is now the phrase, `lala:chat` no longer needs a declared `refusalPhrase`.
// `faq.md` moves in the SAME commit or `scripts/faq-check.mjs` fails the build, by design.
//
// W1-2 — `refusalPhrase` IS THE LABEL WHEN THE LABEL WILL NOT READ IN A SENTENCE. `label` is written
// for the membership page's LIST; the refusal copy puts it at the front of a sentence, and some of
// them break there (a comma inside a noun phrase behind an article). Where a feature declares a phrase, the
// refusal uses it verbatim, article included. Where it does not, the phrase is `the ${label}` with a
// leading "The " folded in. It lives HERE, beside the price and the gate, and the fast gate PRINTS
// every composed sentence every build, because a gate can check that a line names the tier and points
// at Upgrade but it cannot read English.
// ---------------------------------------------------------------------------
export const FEATURES = {
  // ---- FREE, FOREVER (T1-5). Hers from signup, paid or not: read it, keep it, download it. ----
  //
  // THIS IS ABOUT HER OWN SIGNUP READINGS, and only those. Writing them the first time, re-opening
  // them from My Room, re-opening them from a shelf, and downloading them are all the same
  // entitlement — she is looking at the reading the house made her when she walked in.
  //
  // The ASTROLOGY and NUMEROLOGY ROOMS' generators are NOT this. Touching a NEW reading, a
  // compatibility, or any room generator is `room:astrology` / `room:numerology` below and stays
  // gated at Newbie exactly as ruled.
  "report:birth-chart": { tier: "free", label: "Your Birth Chart report", free: true, enforcedIn: "handler" },
  "report:numerology": { tier: "free", label: "Your Numerology report", free: true, enforcedIn: "handler" },
  // N18-1: human design is not written at signup, but its FIRST TAP from the My Room card is free
  // by the owner's ruling. The card invites the tap; the tap must not raise a box.
  "report:human-design": { tier: "free", label: "Your Human Design report, on your first tap", free: true, enforcedIn: "handler" },
  // FREE-1 (her clause 3, 2026-08-11): **THE SHELVES ARE FREE, HER EXPLICIT WORD.** It moved here
  // from NEWBIE, and the move is one line because T8-1 had already established the only fact that
  // matters: Shelves has NO SERVING ROUTE and must not grow one. Every route behind that drawer
  // (/api/room/shelf, /api/astrology/reports) is already free by law under T1-5 — reading and
  // keeping what she already owns is not a purchase — so the tier here has only ever governed the
  // DRAWER'S PRESENTATION, and `enforcedIn: "shell"` says so. Nothing member-facing behind it
  // changed; what changed is that the shell no longer refuses a free explorer.
  //
  // It is written as a real free-layer entry (`tier: "free"`, `free: true`) rather than left at
  // newbie with the client wall quietly removed, because `canOpen` reads `spec.free` and the
  // membership page, the FAQ and the locked-door box all read this same table: a drawer that opens
  // for an account the table says has not bought it is two answers to one question (R28/R29), and
  // the one nobody can see is the one that rots.
  shelves: { tier: "free", label: "Shelves", free: true, enforcedIn: "shell", refusalPhrase: "the Shelves drawer" },

  // ---- NEWBIE $39 ----
  // >>> **L11 (her order, 2026-08-13) — THE ONE READING THIS HOUSE MADE HER THAT IS *NOT* FREE, AND
  // IT IS A DELIBERATE EXCEPTION TO T1-5 WRITTEN DOWN RATHER THAN QUIETLY TAKEN.** <<<
  //
  // T1-5's block twenty lines up states the opposite rule in as many words — *"she is looking at the
  // reading the house made her when she walked in"*, *"reading and keeping what she already owns is
  // not a purchase"* — and every word of that stays true of her birth chart and her numerology.
  //
  // **THE FUNNEL READING IS A DIFFERENT ARTEFACT AND HER CLAUSE SAYS SO: *"tapping it as a free
  // account fires the subscription popup — THAT IS THE CONVERSION MOMENT."*** It was not written for
  // a member; it was written for a STRANGER OFF AN ADVERTISEMENT, and the one paragraph she has
  // already read is the whole of what that advertisement promised. The rest is the offer.
  //
  // **IT IS ITS OWN KEY RATHER THAN A CHANGE TO `report:birth-chart`**, or her signup readings would
  // be walled by a round about a funnel — the failure R19 calls the silent one, arriving as a paying
  // member's own chart refusing to open (`l11-mutations.mjs` M2 is exactly that edit).
  //
  // `enforcedIn: "handler"`, like the three free report keys, and for the same structural reason:
  // `/api/astrology/reports/:id` serves EVERY type, so a route-level gate here would wall her whole
  // shelf. The handler asks this key only when the row it just read is a funnel reading.
  "report:funnel": { tier: "newbie", label: "The reading that brought you here", enforcedIn: "handler", refusalPhrase: "this reading" },
  "room:astrology": { tier: "newbie", label: "Astrology room" },
  "room:numerology": { tier: "newbie", label: "Numerology room" },
  "room:human-design": { tier: "newbie", label: "Human Design room" },
  "room:palmistry": { tier: "newbie", label: "Palmistry room" },
  "room:dream": { tier: "newbie", label: "Dream Interpretation", refusalPhrase: "Dream Interpretation" },
  "daily:guidance": { tier: "newbie", label: "Daily Guidance", refusalPhrase: "Daily Guidance" },
  "daily:tarot-card": { tier: "newbie", label: "Daily Tarot Card" },
  journal: { tier: "newbie", label: "Journal" },
  // T8-1: the three conjure drawers share ONE route (/api/lala/craft) across THREE tiers, so the
  // handler reads the kind and gates on it. This is the owner's finding A: as a /api/lala/ path it
  // cost `lala:chat` (apprentice), which refused a paid Newbie her own affirmations and handed an
  // Apprentice the Vet incantations in the same stroke.
  "drawer:affirmations": { tier: "newbie", label: "Affirmations", enforcedIn: "handler" },
  // EXECUTIVE DECISION, logged for the owner (2026-07-28): the two ON-DEMAND My Room reports were
  // not named in her tier list. They fail CLOSED by default, which would have put them at Vet and
  // locked a Newbie out of a chart-derived reading sitting in her own room, next to the rooms she
  // just paid for. Placed at NEWBIE as the least surprising reading of her structure. One line to
  // move if she wants them higher.
  "report:personality": { tier: "newbie", label: "Personality & Purpose report", enforcedIn: "handler" },
  "report:compatibility": { tier: "newbie", label: "Compatibility Profile report" },
  // LR1 — THE LIVING ROOM. **NEWBIE-AND-UP: EVERY PAID MEMBER, BY HER WORD (L2.7, 2026-08-05).**
  // It was APPRENTICE from 2026-07-31 until that ruling; her replacement is that the community room,
  // the INBOX/MESSAGES and NOTIFICATIONS are open to everyone who pays — a member who has just paid
  // $39 arrives in a house with other people in it, rather than in an empty one she has to pay again
  // to leave. Free and visitor accounts still meet the warm tier box, which is what this key is for.
  //
  // This line is STILL the ONLY place the room's tier is written down: the page gate (src/lib/pages.ts
  // PAGE_FEATURE, which prices `/inbox` at this same key), the route gate (server/route-features.js's
  // `/api/living-room/` prefix, which is what carries messages AND notifications), the room card, the
  // bottom-bar sheet and the warm box all resolve through `room:living` — so moving the community room
  // between tiers stays what every other room's move is, one line, here.
  "room:living": { tier: "newbie", label: "The Living Room", refusalPhrase: "the Living Room" },

  // ---- APPRENTICE $69 ----
  // Ask Lala unlocks HERE. Its appearance in the Vet list is INHERITANCE, not a second grant.
  "lala:chat": { tier: "apprentice", label: "Ask Lala" },
  library: { tier: "apprentice", label: "The Library" },
  "drawer:verses": { tier: "apprentice", label: "Verses" },
  "drawer:chants": { tier: "apprentice", label: "Chants", enforcedIn: "handler" },

  // ---- VET $149 ----
  // AKASHIC-1 (her ruling, 2026-09-01): *"the Akashic Records Room opens at APPRENTICE and above
  // (Newbie meets the gate with the standing upgrade path), all five readings at that tier."*
  //
  // **ONE KEY FOR THE ROOM AND ALL FIVE READINGS, RATHER THAN FIVE `report:` KEYS.** She priced the
  // ROOM, not the readings, so a per-reading key would be five places to move the day she reprices
  // it and five chances for one to be forgotten (R28/R29). The page gate, the route gate and the
  // room card all resolve through this line, exactly as `room:living` documents for the community
  // room — moving the Akashic room between tiers stays one line, here.
  //
  // `refusalPhrase` is what a Newbie actually reads at the wall, so the standing upgrade path names
  // the room in her own words rather than a feature key.
  "room:akashic": { tier: "apprentice", label: "Akashic Records room", refusalPhrase: "the Akashic Records room" },
  "room:magic": { tier: "vet", label: "Magic Room" },
  // LIVE-1 — her clause is "Vet plus owners", and the VET half is written down HERE and nowhere
  // else, so moving going-live between tiers is one line (`room:living`'s own standing note). The
  // OWNER half needs no law of its own: `resolveEntitlements` opens with
  // `if (member.owner) return entitlementsFor({ owner: true })`, so `requireFeature("room:live")`
  // answers her whole sentence off ONE door and an owner seat never depends on holding a tier it
  // never bought (see `src/liveRoom.js` for the law this round wrote and then deleted).
  "room:live": { tier: "vet", label: "Going live", refusalPhrase: "going live" },
  "room:seal-creator": { tier: "vet", label: "Seal Creator" },
  // >>> **SETTLED BY HER, 2026-09-05: TAROT IS A VET PRIVILEGE AND THE REFUSAL STAYS.** <<<
  // *"Elite members (Apprentice-tier grant) do NOT get tarot pulls, the refusal is correct and
  //  stays... No card access below Vet."* **85 elite seats have been refused a card pull 346 times
  // and every one of those refusals was RIGHT** — it is the tier line, not a defect, and it is
  // written here so no later round reads that number as a bug report. ELITE-SPELL-2 opened the
  // CRAFT to an elite seat (seals, spells) and deliberately did not open this; the daily card
  // `daily:tarot-card` is Newbie and is a different door.
  "room:tarot": { tier: "vet", label: "The full Tarot room" },
  "room:astrocartography": { tier: "vet", label: "Astrocartography", refusalPhrase: "Astrocartography" },
  "drawer:incantations": { tier: "vet", label: "Incantations", enforcedIn: "handler" },
  "drawer:angels": { tier: "vet", label: "Angels" },
};

// ---------------------------------------------------------------------------
// ONE-TIME PRODUCTS (T1-1, T1-7). Separate from tiers entirely: bought once, attached to the
// account FOREVER. A tier change or a cancellation never takes one away — only the member deleting
// her account does. The same mechanism serves every future paid class.
// ---------------------------------------------------------------------------
export const ONE_TIME_PRODUCTS = {
  "spirit-keeping": {
    key: "spirit-keeping",
    label: "Spirit Keeping",
    price: 499,
    priceText: "$499",
    kind: "video",
    where: "library",
    // The catalogue row this unlocks. Named HERE so the playback gate and the Library card agree
    // about which video costs money, rather than each carrying its own copy of the slug.
    videoSlug: "spirit-keeping",
    blurb: "The Spirit Keeping video, in full. Bought once, yours for good.",
  },
};

// ---------------------------------------------------------------------------
// ASK LALA FAIR-USE (T1-8). Turns per day, per tier. ONE LINE TO CHANGE A CAP.
// `null` means uncapped. Owner accounts are uncapped regardless.
// ---------------------------------------------------------------------------
// >>> HER RULING, 2026-08-21: APPRENTICE 40/DAY, VET 70/DAY. <<< It was 30/60. #130's own trace is
// why she moved it: bigevelyn1219@ hit the Apprentice cap on five days in nine, with the ledger
// exact (30 turns, 30 questions, 30 answers, `lala-cap` fired once) — nothing was wrongly consumed
// and nothing was restored, so the number itself was the finding and the number is HERS.
//
// **AND CHANGING IT IS ONE LINE ONLY BECAUSE EVERY READER IS DERIVED FROM IT** — the cap state, the
// notice, the upgrade line's "carries N", the tier cards' `lalaTurnsPerDay` and the turn warning all
// read this table. The one thing that was NOT derived is repaired in `server/lala-cap.js` in the
// same push: the number-spelling table was keyed on 30 and 60 by hand, so this edit alone would have
// degraded her warm sentence to a numeral with nothing red anywhere.
export const LALA_DAILY_TURNS = {
  free: 0,
  newbie: 0,        // Ask Lala is not in Newbie at all; the locked door handles this, not the cap.
  apprentice: 40,
  vet: 70,
};

// The conversation history that rides into each Lala turn. Without a ceiling, a long thread costs
// more on every single turn forever, which is the one cost curve that never flattens on its own.
export const LALA_HISTORY = {
  windowTurns: 12,        // the most recent turns, in full
  summaryAfterTurns: 12,  // beyond that, a compact running summary carries the substance
  summaryMaxWords: 260,
};

// ---------------------------------------------------------------------------
// THE RESOLVERS. Everything above is data; this is the whole of the logic.
// ---------------------------------------------------------------------------

// What a feature costs, and what it is called. Unknown keys FAIL CLOSED to the top tier: a feature
// somebody forgot to register is locked, not accidentally free.
export function featureSpec(feature) {
  return FEATURES[feature] || { tier: "vet", label: String(feature || "this"), unregistered: true };
}

// The tier a member is actually on, from her own stored row. `owner` bypasses everything.
export function memberTier({ owner = false, tier = null, subscribed = false } = {}) {
  if (owner) return "vet";
  if (tier && TIER_ORDER.includes(tier)) return tier;
  // A legacy member with `subscribed` true and no tier row predates the ladder. She keeps what she
  // has always been able to open (everything) until her row is migrated: a paying member must never
  // wake up locked out of something she was using yesterday.
  if (subscribed) return "vet";
  return "free";
}

// THE ONE QUESTION EVERY GATE ASKS.
//
// >>> **EVENT-1 — THE GATE-DROP RIDES HERE, BECAUSE THIS IS THE ONLY DOOR.** <<<
//
// Her live event, 2026-09-09. A drop opens every PAID feature to every signed-up member for a window
// she starts with one tap, and it closes itself.
//
// **IT IS A PARAMETER AND NOT A GLOBAL**, so this function stays pure and drivable: the caller reads
// the window once per request and passes the answer. A module-level flag read from a database would
// make every gate in this house depend on an import with a side effect, and would make this
// function impossible to drive at an arbitrary instant — which is exactly the test that matters.
//
// **IT CANNOT OPEN THE OWNER'S OWN DOORS.** `member.owner` is answered above it and separately; a
// drop makes a free member a MEMBER, never an owner.
export function canOpen(feature, member = {}, { gateDropOpen = false } = {}) {
  if (member.owner) return true;
  const spec = featureSpec(feature);
  if (spec.free) return true;                      // T1-5: the free layer is hers, always
  // >>> **THE DROP OPENS WHAT A PAYWALL CLOSES, AND ONLY THAT.** <<< It is asked AFTER the free
  // layer and BEFORE the tier compare, so a dropped window answers yes to exactly the set of
  // features a paying tier would have answered yes to.
  if (gateDropOpen === true) return true;
  return tierRank(memberTier(member)) >= tierRank(spec.tier);
}

// Everything a tier opens, in ladder order, WITH the inheritance spelled out — this is what the
// membership page and the purple box both render, so what she is shown is what she is sold.
export function tierContents(tierKey) {
  const own = [];
  for (const [key, spec] of Object.entries(FEATURES)) {
    if (spec.tier === tierKey) own.push({ key, label: spec.label });
  }
  const tier = tierByKey(tierKey);
  const inheritsFrom = tier?.inherits ? tierByKey(tier.inherits) : null;
  return {
    tier: tierKey,
    label: tier?.label || tierKey,
    price: tier?.price ?? null,
    priceText: tier?.priceText || "",
    period: tier?.period || "month",
    tagline: tier?.tagline || "",
    inherits: tier?.inherits || null,
    inheritsLabel: inheritsFrom ? `Everything in ${inheritsFrom.label}` : null,
    unlocks: own,
    lalaTurnsPerDay: LALA_DAILY_TURNS[tierKey] ?? 0,
  };
}

// The whole ladder, ready to render.
export const ladder = () => TIERS.map((t) => tierContents(t.key));

// The free layer, spelled out for the client the same way a tier is.
export const freeLayer = () => ({
  tier: "free",
  label: "Free with your room",
  unlocks: Object.entries(FEATURES).filter(([, s]) => s.free).map(([key, s]) => ({ key, label: s.label })),
});

// T8-1 — WHERE A FEATURE'S GATE LIVES, answered from the config. The route-coverage fixture asks
// this instead of carrying its own list of exceptions: a feature that is not enforced by URL has to
// SAY SO here, next to its price, where the person moving it between tiers will see it.
export const enforcedIn = (feature) => featureSpec(feature).enforcedIn || "route";
export const enforcedByRoute = (feature) => enforcedIn(feature) === "route";

// The lowest tier that opens a feature, for the locked-door box.
// Is this video one of the one-time products? Returns the product, or null for an included video.
export function productForVideoSlug(slug) {
  const want = String(slug || "").trim();
  if (!want) return null;
  return Object.values(ONE_TIME_PRODUCTS).find((p) => p.videoSlug === want) || null;
}

export function tierThatOpens(feature) {
  const spec = featureSpec(feature);
  return spec.free ? null : spec.tier;
}

// ---------------------------------------------------------------------------
// W1-2 — THE REFUSAL, COMPOSED FROM THE SAME TABLE THE GATE ENFORCES WITH.
//
// One function, and every no on the site comes out of it. The door she touched, the tier that opens
// it, and the pointer at the button. Nothing hand-rolls a refusal string any more, and the fast gate
// holds the whole of server/ and src/ against the dead ones so nothing starts again.
// ---------------------------------------------------------------------------

// The noun phrase this feature wears in prose. Declared per feature where the list label breaks.
export function refusalPhrase(feature) {
  const spec = featureSpec(feature);
  return spec.refusalPhrase || phraseForLabel(spec.label);
}

// THE REFUSAL COPY for a gated feature, for THIS member. The tier named is the one that actually
// opens the door, read off the same map `canOpen` uses, so a refusal can never sell her the wrong one.
export function refusalCopy(feature) {
  const needs = tierThatOpens(feature);
  return upgradeCopy({ phrase: refusalPhrase(feature), tierLabel: tierByKey(needs)?.label || "" });
}

// THE REFUSAL COPY for a one-time product, which is not a tier and must never be sold as one.
export function productRefusalCopy(productKey) {
  const p = ONE_TIME_PRODUCTS[String(productKey || "")];
  if (!p) return upgradeCopy({});
  return purchaseCopy({ label: p.label, priceText: p.priceText, where: p.where });
}

// ---------------------------------------------------------------------------
// T1-FIX — THE FREE LAYER, RESOLVED SERVER-SIDE.
//
// The bug the owner hit live: My Room routed EVERY report chip through one membership check that
// had never heard of the free layer, so a zero-tier member tapping her OWN signup Birth Chart was
// shown the membership box. Reading the reading the house made her when she walked in is not a
// purchase, and it never asks her for one.
//
// The fix is HERE, in the map, not in a patch at the chip. Which report types are free is one
// question with one answer, and the SERVER answers it: the client renders what it is told, so a
// client that lies to itself changes nothing about what it is actually allowed to do.
// ---------------------------------------------------------------------------

// Report types a member may generate, open, re-open and download at ANY tier, including none.
export const FREE_REPORT_TYPES = Object.entries(FEATURES)
  .filter(([key, spec]) => spec.free && key.startsWith("report:"))
  .map(([key]) => key.slice("report:".length));

export const isFreeReportType = (type) => FREE_REPORT_TYPES.includes(String(type || ""));

// LAUNCH RULING (2026-08-05, hers) — WHAT A MID-CYCLE UPGRADE COSTS, AND WHEN.
//
// "Mid-cycle upgrades charge only the DIFFERENCE. An Apprentice ($69) upgrading to Vet ($149)
// mid-month pays the prorated gap IMMEDIATELY, the Vet tier activates INSTANTLY, and the next
// billing cycle bills the full new price. Never a second full-price checkout stacked on the first.
// Nobody ever pays twice for the same month. Downgrades take effect at period end, no mid-cycle
// refund."
//
// It lives HERE, with the prices it is about, rather than as a literal inside the portal-configuration
// call in `server/index.js`, for the reason this file exists at all: money law is data, in one place,
// and a value typed at its one call site is a value no gate can read. `server/index.js` imports it.
//
// **WHY THE VALUE MATTERS AND WHY THE WRONG ONE SURVIVES:** both of Stripe's mid-cycle behaviours
// activate the new tier instantly, so the screen is identical either way. `create_prorations` leaves
// the gap as LINES on her next invoice — the next bill is then the new price PLUS the gap, which is
// not what she ruled. `always_invoice` raises and attempts an invoice for the difference NOW. What
// separates them is only WHEN she is charged, and the only surface that says so is her invoice.
export const PRORATION_BEHAVIOR = "always_invoice";

// The upgrade path modifies the subscription she already has (Q4/T8-6). A DOWNGRADE is deliberately
// NOT this value's business: her ruling is that it takes effect at period end with no mid-cycle
// refund, which is Stripe's `cancel_at_period_end`-shaped behaviour on the item rather than a
// proration setting, and it is named here so the two are never conflated by a later round.
export const DOWNGRADE_TAKES_EFFECT = "period-end";

// CANCEL-1 (2026-09-03) — **WHAT CANCELLING MEANS, AS DATA, BESIDE THE PRICES IT IS ABOUT.**
//
// **THIS IS NOT A NEW RULING AND IT WAS NOT CHOSEN BY THE ROUND THAT NEEDED IT.** It is the one this
// house already enforces, written where a gate can read it instead of only where a webhook acts on
// it: `server/stripe-webhook.js` handles a cancellation with the comment *"A cancellation does not
// take back the period she paid for"*, and `DOWNGRADE_TAKES_EFFECT` above says the same thing about
// the neighbouring act. **A member who cancels keeps what she paid for until the period ends**, so
// the portal is configured `at_period_end` and prorates nothing.
//
// It lives here for the reason `PRORATION_BEHAVIOR` does — money law is data, in one place, and a
// value typed at its one call site is a value no gate can read — and it is TWO constants because
// Stripe takes two fields and conflating them is how a mode change silently keeps an old proration.
export const CANCEL_TAKES_EFFECT = "at_period_end";
export const CANCEL_PRORATION_BEHAVIOR = "none";

// Everything the client needs to render the right doors, decided here. `free` is the list above;
// `features` is a resolved yes/no per feature for THIS member, so no rule is ever re-implemented
// on the client where it could drift from this file.
export function entitlementsFor(member = {}) {
  const tier = memberTier(member);
  const features = {};
  for (const key of Object.keys(FEATURES)) features[key] = canOpen(key, member);
  return {
    tier,
    owner: Boolean(member.owner),
    freeReportTypes: FREE_REPORT_TYPES,
    features,
    // W1-1 — THE BOX PAYLOAD FOR A DOOR SHE HAS NOT OPENED YET, DECIDED HERE.
    //
    // The client has to raise the tier box BEFORE it mounts a gated surface (her law: locked means
    // closed, the surface never opens behind the box). To do that it needs what the 402 refusal
    // carries — which door, what it is called, which tier opens it — WITHOUT making the request that
    // would refuse. That is exactly the payload below, and it is built from the same FEATURES table
    // the gate enforces with, so the box she is shown before the request and the box she is shown
    // after one can never disagree. The client re-implements nothing.
    locks: lockPayloads(member),
    lalaTurnsPerDay: member.owner ? null : (LALA_DAILY_TURNS[tier] ?? 0),
  };
}

// One entry per feature this member may NOT open. An allowed feature has no lock, so the client's
// question is simply "is there a lock on this door", and a feature nobody registered fails CLOSED
// here the same way `featureSpec` fails closed everywhere else.
export function lockPayloads(member = {}) {
  const locks = {};
  for (const [key, spec] of Object.entries(FEATURES)) {
    if (canOpen(key, member)) continue;
    locks[key] = { feature: key, featureLabel: spec.label, needsTier: spec.tier, tier: memberTier(member) };
  }
  return locks;
}

// R213 — **THE BOX SHE MEETS BEFORE THE REQUEST WAS NOT THE BOX SHE MEETS AFTER ONE, AND THE COMMENT
// ABOVE `locks:` SAYS IN AS MANY WORDS THAT THEY CANNOT DISAGREE.**
//
// L15 gave `requireFeature`'s 402 its `frozen`/`frozenTier` and **left these locks exactly as they
// were** — so the two payloads that render the SAME purple box stopped saying the same thing. And
// the one that was left behind is the one a member actually meets: **W1-1's whole design is that the
// client refuses FIRST**, raising the box out of `featureLockFor` WITHOUT making the request that
// would refuse (that is what keeps a locked surface from mounting behind it). So a frozen year-long
// Vet met *COME ON IN / Choose your membership* at nearly every door on this site, with the warm
// reactivation screen reachable only from the handful of doors that fetch before they check.
//
// **MEASURED AT COORDINATES BY `l19-recovery-walk.mjs`, AT BOTH VIEWPORTS**, and it is why L15's
// member-facing half STILL did not render after L19 repaired `accessFrozen`'s clock: two rounds each
// fixed one half of the same absence. **The pairing is what made it a finding rather than a
// suspicion** — a member who has never paid a penny met a byte-identical box.
//
// The freeze is decorated HERE, in one declaration read by the one composer, rather than at each
// lock's construction: `lockPayloads` answers *which doors are shut*, which is a fact about the
// FEATURES table, and the freeze is a fact about the member that only `resolveEntitlements` knows.
// **Both fields are always written, exactly as the 402 always writes them** (`false`/`"free"` for
// everybody else), so the two payloads are one shape and a gate can hold that as a biconditional
// rather than as two rosters somebody keeps in step.
export function freezeLocks(locks, { frozen = false, frozenTier = "free" } = {}) {
  const out = {};
  for (const [key, lock] of Object.entries(locks || {})) {
    out[key] = { ...lock, frozen: Boolean(frozen), frozenTier: frozen ? (frozenTier || "free") : "free" };
  }
  return out;
}

// WALL-1 — **AND THE GIFT IS THE SAME ABSENCE ONE PAYLOAD OVER, MEASURED ON HER OWN MEMBERS.**
//
// R213 above is about the freeze; this is the identical hole for a member holding a COMPLIMENTARY
// grant. `giftTruth` has told her account menu *"A gift from the house. Nothing is charged for it."*
// since D2 shipped — and **it reaches that one surface and nothing else.** So a member holding an
// Elite 2026 grant, refused at a door her grant does not open, met *CHOOSE YOUR MEMBERSHIP* with
// **nothing anywhere naming the membership she already holds.**
//
// **MEASURED, NOT REASONED (R25/R180): 386 live elite-2026 grants across 277 distinct members, every
// one of them at tier `apprentice`, and 104 of those 277 ALSO hold a paid Vet subscription.** That
// second number is #139's own control: her mother is Elite 2026 *and* pays for Vet, so the two of
// them meet the same Vet door with the same words on their membership and different answers — which
// reads as a glitch and was reported as one. Three tickets in one week say it in those words:
// *"there's a glitch with my membership"*, *"I'm asking about something that I should already be
// getting"*, *"because I'm a elite 2026 member it should be within my membership"*.
//
// **THE ENTITLEMENT RESOLUTION IS CORRECT AND IS NOT WHAT MOVED (R19/R141).** `combineAccess` reads
// the grant, `resolveEntitlements` resolves apprentice, and the gate refuses a Vet tool exactly as
// it should — every one of those turns carries `tier: "apprentice"` on its own gate row. What was
// wrong is that **the refusal had one sentence for two different facts (R18):** *she has not paid*
// and *she holds a complimentary membership and has reached past it* are not the same thing, and
// only the second one is true of these members.
//
// Decorated HERE for R213's own reason: `lockPayloads` answers *which doors are shut*, which is a
// fact about the FEATURES table, and the gift is a fact about the member that only
// `resolveEntitlements` knows. **Both fields are always written** (`false`/`"free"` for everybody
// else), so the lock payload and the 402 are ONE SHAPE and the gate holds it as a biconditional
// rather than as two rosters somebody keeps in step.
//
// **THE FREEZE AND THE GIFT CANNOT BOTH BE TRUE, AND THAT IS STRUCTURAL RATHER THAN AN ORDERING.**
// L15's `frozenNow` is `frozen && access.tier === "free"`, and a live grant makes `access.tier`
// non-free — so a gifted member is never frozen. It is asserted rather than branched on, so a later
// change to either law goes red instead of quietly showing her two contradictory sentences.
export function giftLocks(locks, { gift = false, giftTier = "free" } = {}) {
  const out = {};
  for (const [key, lock] of Object.entries(locks || {})) {
    out[key] = { ...lock, gift: Boolean(gift), giftTier: gift ? (giftTier || "free") : "free" };
  }
  return out;
}

// =================================================================================================
// >>> **ITEM 11(b) — HER SELF-DOWNGRADE RULING, AS A LAW BEFORE IT IS A DOOR.** <<< (2026-09-03)
//
// *"Self-downgrade: YES, members may move DOWN a tier themselves (period-end effective, the trust
//  brand keeps its door honest, blocking downgrades breeds chargebacks)."*
//
// **`DOWNGRADE_TAKES_EFFECT` HAS BEEN DECLARED ABOVE SINCE CANCEL-1 AND HAS HAD NO DOOR TO GOVERN.**
// This is the law that reads it: *may this member move to that tier, and what happens when.*
//
// >>> **IT IS PURE AND IT REFUSES IN FIVE DIFFERENT WAYS, BECAUSE EVERY ONE OF THEM SENDS HER
// SOMEWHERE ELSE (R18).** <<< *You are already there*, *that is up, not down*, *we do not carry that
// tier*, *you have nothing to move* and *your seat is frozen* are five different sentences, and a
// door that answered one refusal to all five would be the membership page telling a frozen member
// she is already on Newbie.
// =================================================================================================

/** What a downgrade may answer. Declared, so a caller cannot invent a sixth outcome. */
export const DOWNGRADE_VERDICTS = Object.freeze({
  ok: "ok",
  alreadyThere: "already-there",
  notDown: "not-down",
  unknownTier: "unknown-tier",
  noSubscription: "no-subscription",
  frozen: "frozen",
});

/**
 * >>> **MAY SHE MOVE DOWN, AND TO WHAT?** <<<
 *
 * `from` is the tier she is on, `to` is the one she picked, `hasSubscription` is whether there is a
 * real Stripe subscription to reschedule, and `frozen` is L15's state.
 *
 * **A GRANTED SEAT IS NOT A DOWNGRADE (it is `no-subscription`).** A complimentary Apprentice has
 * nothing billing to move, and telling her to "move down" would offer her a change she cannot make
 * and does not need — the same defect shape as offering the menu and refusing what she picks.
 *
 * **AND `free` IS NOT A DESTINATION.** Moving to free is CANCELLING, which has its own door, its own
 * ruling and its own words. Two doors for one act would be two answers to what leaving means
 * (R28/R29), so this refuses it as `unknown-tier` and the caller sends her to the cancel door.
 */
export function downgradeVerdict({ from = "", to = "", hasSubscription = false, frozen = false } = {}) {
  const here = String(from || "").trim();
  const there = String(to || "").trim();
  // The freeze is asked FIRST: a frozen member is not choosing a tier, she is waiting on a card, and
  // every other refusal below would be a true sentence about the wrong subject (L15's own shape).
  if (frozen === true) return { verdict: DOWNGRADE_VERDICTS.frozen, takesEffect: null };
  if (!TIER_ORDER.includes(there) || there === "free") return { verdict: DOWNGRADE_VERDICTS.unknownTier, takesEffect: null };
  if (!TIER_ORDER.includes(here)) return { verdict: DOWNGRADE_VERDICTS.unknownTier, takesEffect: null };
  if (here === there) return { verdict: DOWNGRADE_VERDICTS.alreadyThere, takesEffect: null };
  if (TIER_ORDER.indexOf(there) > TIER_ORDER.indexOf(here)) return { verdict: DOWNGRADE_VERDICTS.notDown, takesEffect: null };
  if (hasSubscription !== true) return { verdict: DOWNGRADE_VERDICTS.noSubscription, takesEffect: null };
  return { verdict: DOWNGRADE_VERDICTS.ok, takesEffect: DOWNGRADE_TAKES_EFFECT };
}

/** Every tier this member could move DOWN to, derived from the ladder rather than typed (R11/R31). */
export function downgradeTargets(from = "") {
  const i = TIER_ORDER.indexOf(String(from || "").trim());
  if (i <= 0) return [];
  // `free` is never a target: leaving is the cancel door's act, and it has its own words.
  return TIER_ORDER.slice(0, i).filter((t) => t !== "free");
}

/**
 * **WHAT SHE IS TOLD, PER VERDICT.** One sentence each, in the house's voice, and every refusal names
 * the door that CAN help her — G-DEADEND's law: every no carries a door.
 */
export function downgradeMessage(verdict, { label = "", when = "" } = {}) {
  switch (verdict) {
    case DOWNGRADE_VERDICTS.ok:
      return `You are moving to ${label}${when ? ` on ${when}` : " when your month runs out"}. Nothing changes before then and you keep everything you have already paid for.`;
    case DOWNGRADE_VERDICTS.alreadyThere:
      return `You are already on ${label}.`;
    case DOWNGRADE_VERDICTS.notDown:
      return `That one is a step up rather than down. Tap it on the membership page and it will take you through the change.`;
    case DOWNGRADE_VERDICTS.noSubscription:
      return "There is no billing to move on your seat, so there is nothing to change. If you want to stop the membership itself, that is under Manage billing.";
    case DOWNGRADE_VERDICTS.frozen:
      return "Your membership is on hold while a payment is sorted out. Once that clears you can move it wherever you want.";
    default:
      return "That is not a membership we carry. If you want to stop the membership itself, tap Member at the top, then Manage billing.";
  }
}
